// One area's maintenance on the Take Off (#515, mock-up maintenance-pricing.html): the template's
// "Maintenance Servicing" columns for each serviced row, with its Extra Service (None, Normal or
// Extra %, MJ 23 Oct 2026), and the area's Schedule of Work, one box per contract year. Shown under
// the area's repaint rows; the repaint itself is unchanged.
import { useState } from 'react';
import { setServiceExtra, washLine, type MaintenanceChoice, type OptionScope, type SectionContract, type YearWork } from '@higgins/m2q-pricing';
import { IN_FIELD_ORDER, moveToNextField } from '../lib/field-order';
import { yearWorkChange } from '../lib/maintenance-edits';
import { NumberField } from './number-field';
import { formatMoney, formatTwo } from './surface-format';
import { CHANGED, COLUMN_COUNT, NUM, PINNED_ROW } from './take-off-rows';

/** Wash replaces the service in a year, on external areas only (DD, 23 Oct 2026; #510's side rule). */
const WASH_SIDE = 'External';
const WORK_LABEL: Record<YearWork, string> = { repaint: 'Repaint', service: 'Service', none: 'No Work', wash: 'Wash' };
const SELECT = 'h-7 rounded border border-slate-300 px-1 text-xs';

type Level = 'none' | 'normal' | 'extra';
const LEVEL_LABEL: Record<Level, string> = { none: 'None', normal: 'Normal', extra: 'Extra %' };

interface ExtraServiceProps {
  name: string;
  /** This option's choice for the row; undefined = the set-up's Extra Servicing %. */
  stored: number | 'none' | undefined;
  /** The set-up's Extra Servicing %, as the number. */
  setupPercent: number;
  onChange: (choice: number | 'none' | null) => void;
}

const levelOf = (value: number | 'none'): Level => (value === 'none' ? 'none' : value > 0 ? 'extra' : 'normal');

/** A row's Extra Service: None, Normal, or Extra with its %; clearing the % goes back to the set-up's. */
function ExtraService({ name, stored, setupPercent, onChange }: ExtraServiceProps) {
  const [choosingExtra, setChoosingExtra] = useState(false);
  const level = choosingExtra ? 'extra' : levelOf(stored ?? setupPercent);
  const change = (next: Level) => {
    setChoosingExtra(next === 'extra' && levelOf(stored ?? setupPercent) !== 'extra');
    if (next === 'none') onChange('none');
    if (next === 'normal') onChange(0);
  };
  return (
    <span className="flex items-center justify-end gap-1">
      <select
        {...IN_FIELD_ORDER}
        onKeyDown={(e) => moveToNextField(e)}
        value={level}
        onChange={(e) => change(e.target.value as Level)}
        aria-label={`${name} servicing`}
        className={`${SELECT} ${stored !== undefined ? CHANGED : 'bg-white'}`}
      >
        {(Object.keys(LEVEL_LABEL) as Level[]).map((l) => (
          <option key={l} value={l}>
            {LEVEL_LABEL[l]}
          </option>
        ))}
      </select>
      {level === 'extra' && (
        <>
          <NumberField
            compact
            value={typeof stored === 'number' && stored > 0 ? stored : null}
            placeholder={String(setupPercent)}
            onChange={(v) => onChange(v)}
            label={`${name} extra servicing %`}
            className="w-12"
          />
          %
        </>
      )}
    </span>
  );
}

interface Props {
  areaId: string;
  side: string | null;
  section: SectionContract;
  choice: MaintenanceChoice | undefined;
  startYear: number;
  onScope: (change: (scope: OptionScope) => OptionScope) => void | Promise<void>;
}

const HEADINGS = ['Second Repaint Hours', 'Litres', 'Extra Service', 'Service Hrs', 'Litres', 'Rate', 'Sell Price'];

export function MaintenanceSection({ areaId, side, section, choice, startYear, onScope }: Props) {
  const detail = section.service.detail;
  const own = choice?.serviceExtra?.[areaId] ?? {};
  const setupPercent = choice?.extraServicing ?? 0;
  const stored = choice?.programme?.[areaId] ?? [];
  const works: YearWork[] = side === WASH_SIDE ? ['repaint', 'service', 'none', 'wash'] : ['repaint', 'service', 'none'];
  const count = (work: YearWork) => section.programme.filter((w) => w === work).length;
  const made = (['repaint', 'service', 'wash'] as const).filter((w) => count(w) > 0).map((w) => `${count(w)} ${WORK_LABEL[w].toLowerCase()}${count(w) > 1 ? 's' : ''}`);

  return (
    <tbody className="bg-purple-50/40">
      <tr>
        <td colSpan={COLUMN_COUNT} className="px-2 py-2">
          <div className={`${PINNED_ROW} space-y-2`}>
            <table className="text-xs">
              <thead>
                <tr className="text-slate-500">
                  <th className="px-1.5 text-left font-semibold text-slate-800">Maintenance Servicing <span className="font-normal">(one year)</span></th>
                  {HEADINGS.map((h, i) => (
                    <th key={`${h}${i}`} className="px-1.5 text-right font-normal">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {detail.rows.map((row) => (
                  <tr key={row.id} className="border-t border-purple-100">
                    <td className="px-1.5 py-0.5">{row.name}</td>
                    <td className={NUM}>{formatTwo(row.secondHours)}</td>
                    <td className={NUM}>{row.secondLitres ? formatTwo(row.secondLitres) : ''}</td>
                    <td className="px-1.5 py-0.5">
                      <ExtraService
                        name={row.name}
                        stored={own[row.id]}
                        setupPercent={setupPercent}
                        onChange={(value) => onScope((s) => setServiceExtra(s, areaId, row.id, value))}
                      />
                    </td>
                    <td className={NUM}>{row.extra <= -1 ? '–' : formatTwo(row.hours)}</td>
                    <td className={NUM}>{row.extra <= -1 || !row.litres ? '' : formatTwo(row.litres)}</td>
                    <td className={NUM}>{formatTwo(row.rate)}</td>
                    <td className={NUM}>{formatMoney(row.sell)}</td>
                  </tr>
                ))}
                {[
                  ['Hire and scaffold moves (with access)', detail.hireSell],
                  ['Fuel levy and other trades (with prelims)', detail.prelimsSell],
                  ['Negotiating factor', detail.negotiating],
                  ['Abseil (the template’s V89)', detail.abseilSell],
                ]
                  .filter(([, sell]) => sell !== 0)
                  .map(([label, sell]) => (
                    <tr key={label as string} className="border-t border-purple-100 text-slate-600">
                      <td className="px-1.5 py-0.5" colSpan={7}>
                        {label}
                      </td>
                      <td className={NUM}>{formatMoney(sell as number)}</td>
                    </tr>
                  ))}
                <tr className="border-t border-purple-200 font-semibold">
                  <td className="px-1.5 py-0.5" colSpan={4}>
                    One year&rsquo;s servicing
                  </td>
                  <td className={NUM}>{formatTwo(detail.hours)}</td>
                  <td className={NUM}>{formatTwo(detail.litres)}</td>
                  <td />
                  <td className={NUM}>{formatMoney(detail.sell)}</td>
                </tr>
              </tbody>
            </table>
            <div className="flex flex-wrap items-end gap-2 text-xs">
              <span className="self-center font-semibold text-slate-800">Schedule of Work</span>
              {section.programme.map((work, i) => (
                <label key={i} className="flex flex-col text-slate-500">
                  {startYear + i} · {i + 1}
                  <select
                    {...IN_FIELD_ORDER}
                    onKeyDown={(e) => moveToNextField(e)}
                    value={work}
                    onChange={yearWorkChange(onScope, areaId, i + 1)}
                    aria-label={`${section.name} year ${i + 1}`}
                    className={`${SELECT} ${stored[i] ? CHANGED : 'bg-white'} ${work === 'none' ? 'text-slate-500' : 'text-slate-900'}`}
                  >
                    {works.map((w) => (
                      <option key={w} value={w}>
                        {WORK_LABEL[w]}
                      </option>
                    ))}
                  </select>
                  {/* Where a Wash year's figure comes from (DD, 23 Oct): the area's wash m², else its surfaces' total. */}
                  {work === 'wash' && <span className="text-slate-600">{washLine(section.wash.area)}</span>}
                </label>
              ))}
              <span className="self-center text-slate-600">
                Area over the contract: {made.join(' + ') || 'no work'} = <strong className="tabular-nums">{formatMoney(section.sell)}</strong>
              </span>
            </div>
          </div>
        </td>
      </tr>
    </tbody>
  );
}
